import { useLayoutEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { formatEvaluation } from "@chessla/shared";
import type { AnalyzedMoveResult } from "@/shared/api/engine.api";
import { buildGameEvidenceTimeline, type GameEvidencePoint } from "./game-evidence-timeline";
import { boardMotifColor } from "./BoardMotifBadge";

interface Props {
  moves: AnalyzedMoveResult[];
  playerColor: "w" | "b";
  currentIndex: number;
  showMotifs: boolean;
  includeOpponentEvidence?: boolean;
  onSelect: (index: number) => void;
}

const DEFAULT_WIDTH = 760,
  H = 246,
  PAD = 8,
  EQUALITY_Y = 72,
  THINK_TOP = 148,
  THINK_BASE = 220;
const PAPER = "#f0ece2",
  INK = "#0a0a0a",
  CHARCOAL = "#292723",
  RED = "#ff3d12";
const xAt = (index: number, count: number, width: number) => PAD + (index * (width - PAD * 2)) / Math.max(1, count - 1);
const score = (point: GameEvidencePoint) =>
  point.evaluation?.mate != null
    ? Math.sign(point.evaluation.mate) * 600
    : Math.max(-600, Math.min(600, point.evaluation?.cp ?? 0));
export const evaluationY = (point: GameEvidencePoint, playerColor: "w" | "b") =>
  EQUALITY_Y - score(point) * 0.075 * (playerColor === "w" ? 1 : -1);
export const nearestTimelinePoint = (x: number, width: number, count: number) =>
  Math.max(0, Math.min(count - 1, Math.round(((x - PAD) / Math.max(1, width - PAD * 2)) * Math.max(0, count - 1))));
export const timelineGuideX = (pointerX: number | null, point: GameEvidencePoint, count: number, width: number) =>
  pointerX ?? xAt(point.index, count, width);
const smoothCurve = (x1: number, y1: number, x2: number, y2: number) => {
  const control = (x2 - x1) * 0.38;
  return `C ${x1 + control} ${y1}, ${x2 - control} ${y2}, ${x2} ${y2}`;
};
const smoothSegment = (x1: number, y1: number, x2: number, y2: number) =>
  `M ${x1} ${y1} ${smoothCurve(x1, y1, x2, y2)}`;
const classifiedMotifs = [
  "checkmate",
  "brilliant",
  "book",
  "best",
  "excellent",
  "good",
  "blunder",
  "opponent_blunder",
  "mistake",
  "opponent_mistake",
  "imprecision",
  "opponent_imprecision",
  "time_pressure",
  "low_time",
];
const primaryMotif = (motifs: string[]) => classifiedMotifs.find((name) => motifs.includes(name)) ?? motifs[0] ?? null;
export const timelineMotifColor = boardMotifColor;
export const evidencePointLabel = (point: GameEvidencePoint) =>
  [
    `Move ${point.moveNumber} ${point.san}`,
    point.evaluation ? `White evaluation ${formatEvaluation(point.evaluation)}` : "Evaluation unavailable",
    point.thinkSeconds == null ? "Time unavailable" : `${point.moverColor} ${point.thinkSeconds}s think time`,
    point.motifs.length ? `Motifs: ${point.motifs.join(", ")}` : "No verified motif",
  ].join(" · ");
export const thinkTimePointLabel = (point: GameEvidencePoint) => [
  `Move ${point.moveNumber} ${point.san}`,
  point.thinkSeconds == null
    ? `${point.moverColor[0].toUpperCase()}${point.moverColor.slice(1)} time unavailable`
    : `${point.moverColor[0].toUpperCase()}${point.moverColor.slice(1)} think time ${point.thinkSeconds}s`,
];
export const thinkBarWidth = (width: number, moveCount: number) =>
  Math.max(7, Math.min(12, ((width - PAD * 2) / Math.max(1, moveCount)) * 0.24));

export const GameEvidenceTimeline = ({
  moves,
  playerColor,
  currentIndex,
  showMotifs,
  includeOpponentEvidence = false,
  onSelect,
}: Props) => {
  const points = buildGameEvidenceTimeline(moves, includeOpponentEvidence);
  const chartRoot = useRef<HTMLDivElement | null>(null);
  const [chartWidth, setChartWidth] = useState(DEFAULT_WIDTH);
  const [activePoint, setActivePoint] = useState<GameEvidencePoint | null>(null);
  const [hoverKind, setHoverKind] = useState<"evidence" | "time">("evidence");
  const [pointer, setPointer] = useState<{ x: number; y: number; ratio: number } | null>(null);
  useLayoutEffect(() => {
    const root = chartRoot.current;
    if (!root) return;
    const measure = () => setChartWidth(Math.max(280, Math.round(root.getBoundingClientRect().width)));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(root);
    return () => observer.disconnect();
  }, []);
  if (points.length < 2) return null;
  const evaluated = points.filter(({ evaluation }) => evaluation != null);
  const regions = evaluated.slice(0, -1).flatMap((point, index) => {
    const next = evaluated[index + 1];
    const x1 = xAt(point.index, points.length, chartWidth),
      x2 = xAt(next.index, points.length, chartWidth),
      y1 = evaluationY(point, playerColor),
      y2 = evaluationY(next, playerColor),
      s1 = score(point),
      s2 = score(next);
    const region = (side: "white" | "black", startX: number, startY: number, endX: number, endY: number) => ({
      side,
      fillPath: `M ${startX} ${EQUALITY_Y} L ${startX} ${startY} ${smoothCurve(startX, startY, endX, endY)} L ${endX} ${EQUALITY_Y} Z`,
      line: smoothSegment(startX, startY, endX, endY),
      fill: side === "white" ? PAPER : CHARCOAL,
      lineColor: side === "white" ? PAPER : CHARCOAL,
    });
    if (s1 === 0 || s2 === 0 || Math.sign(s1) === Math.sign(s2))
      return [region((s1 + s2) / 2 >= 0 ? "white" : "black", x1, y1, x2, y2)];
    const ratio = Math.abs(s1) / (Math.abs(s1) + Math.abs(s2));
    const crossing = x1 + (x2 - x1) * ratio;
    return [
      region(s1 > 0 ? "white" : "black", x1, y1, crossing, EQUALITY_Y),
      region(s2 > 0 ? "white" : "black", crossing, EQUALITY_Y, x2, y2),
    ];
  });
  const maxThink = Math.max(1, ...points.map(({ thinkSeconds }) => thinkSeconds ?? 0));
  const moveNumbers = [...new Set(points.map(({ moveNumber }) => moveNumber))];
  const playerSide = playerColor === "w" ? "white" : "black";
  const guidePoint = activePoint ?? points[currentIndex] ?? null;
  const tooltipLines = activePoint
    ? hoverKind === "time"
      ? thinkTimePointLabel(activePoint)
      : evidencePointLabel(activePoint).split(" · ")
    : [];

  return (
    <Box
      component="section"
      aria-labelledby="game-evidence-title"
      sx={{ mt: 1.5, borderTop: "1px solid", borderColor: "divider", pt: 1.25, minWidth: 0, overflow: "clip" }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 0.5, mb: 0.5 }}>
        <Typography id="game-evidence-title" sx={{ fontSize: ".72rem", fontWeight: 900, letterSpacing: ".08em" }}>
          GAME EVIDENCE
        </Typography>
        <Typography sx={{ color: "text.secondary", fontSize: ".68rem" }}>
          EVAL · WHITE / BLACK THINK TIME · MOTIFS
        </Typography>
      </Box>
      <Box ref={chartRoot} sx={{ position: "relative", width: "100%", minWidth: 0 }}>
        {activePoint && (
          <Box
            role="tooltip"
            data-testid="evidence-tooltip"
            data-tooltip-kind={hoverKind}
            sx={{
              position: "absolute",
              zIndex: 3,
              ...(pointer
                ? {
                    left: `${pointer.ratio * 100}%`,
                    top: 6,
                    transform: pointer.ratio > 0.62 ? "translateX(calc(-100% - 12px))" : "translateX(12px)",
                  }
                : { top: 4, right: 4 }),
              maxWidth: "min(300px, 86%)",
              bgcolor: "background.default",
              border: "1px solid",
              borderColor: "secondary.main",
              p: 1.5,
              boxShadow: "5px 5px 0 rgba(255,61,18,.28)",
              pointerEvents: "none",
            }}
          >
            {tooltipLines.map((line, index) => (
              <Typography
                key={line}
                sx={{
                  mt: index ? 0.45 : 0,
                  color: index ? "text.secondary" : "text.primary",
                  fontSize: index ? ".61rem" : ".68rem",
                  fontWeight: index ? 700 : 950,
                  lineHeight: 1.45,
                  letterSpacing: index ? ".035em" : ".07em",
                  textTransform: "uppercase",
                }}
              >
                {line}
              </Typography>
            ))}
          </Box>
        )}
        <Box
          component="svg"
          data-player-perspective={playerColor === "w" ? "white" : "black"}
          viewBox={`0 0 ${chartWidth} ${H}`}
          role="img"
          aria-label={`Evaluation from ${playerColor === "w" ? "White" : "Black"} perspective and think-time timeline. Select a point to open that ply.`}
          onPointerMove={(event) => {
            const svg = event.currentTarget;
            const bounds = svg.getBoundingClientRect();
            const screenPoint = svg.createSVGPoint();
            screenPoint.x = event.clientX;
            screenPoint.y = event.clientY;
            const matrix = svg.getScreenCTM();
            const local = matrix
              ? screenPoint.matrixTransform(matrix.inverse())
              : {
                  x: ((event.clientX - bounds.left) / Math.max(1, bounds.width)) * chartWidth,
                  y: ((event.clientY - bounds.top) / Math.max(1, bounds.height)) * H,
                };
            const x = Math.max(8, Math.min(chartWidth - 8, local.x));
            const y = Math.max(0, Math.min(H, local.y));
            const ratio = Math.max(0, Math.min(1, (event.clientX - bounds.left) / Math.max(1, bounds.width)));
            setHoverKind((event.target as Element).closest("[data-think-point]") ? "time" : "evidence");
            setActivePoint(points[nearestTimelinePoint(x, chartWidth, points.length)] ?? null);
            setPointer({ x, y, ratio });
          }}
          onPointerLeave={() => {
            setActivePoint(null);
            setPointer(null);
            setHoverKind("evidence");
          }}
          sx={{ display: "block", width: "100%", height: H, touchAction: "pan-y", overflow: "hidden" }}
        >
          {regions.map((region, index) => (
            <path
              key={`region-${index}`}
              data-eval-region={region.side}
              data-eval-fill="solid"
              d={region.fillPath}
              fill={region.fill}
              stroke={region.fill}
              strokeWidth="1.5"
              strokeLinejoin="round"
            />
          ))}
          {regions.map((region, index) => (
            <path
              key={`line-${index}`}
              data-eval-line={region.side}
              d={region.line}
              fill="none"
              stroke={region.lineColor}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          ))}
          <line
            data-eval-zero-line="true"
            data-chart-band="evaluation"
            x1={PAD}
            y1={EQUALITY_Y}
            x2={chartWidth - PAD}
            y2={EQUALITY_Y}
            stroke={PAPER}
            strokeWidth="3"
            strokeLinecap="round"
            opacity=".5"
            vectorEffect="non-scaling-stroke"
          />
          <line x1={PAD} y1={132} x2={chartWidth - PAD} y2={132} stroke={PAPER} opacity=".16" />
          <g data-chart-band="think-time">
            {points.map((point) => {
              const group = moveNumbers.indexOf(point.moveNumber);
              const center = xAt(group, moveNumbers.length, chartWidth);
              const white = point.moverColor === "white";
              const height = ((point.thinkSeconds ?? 0) / maxThink) * (THINK_BASE - THINK_TOP);
              const label = `Move ${point.moveNumber} ${point.san} · ${point.moverColor} think time ${point.thinkSeconds ?? "unavailable"}${point.thinkSeconds == null ? "" : " seconds"}`;
              const width = thinkBarWidth(chartWidth, moveNumbers.length);
              const x = center + (white ? -width - 1 : 1);
              return (
                <g
                  key={`time-${point.index}`}
                  role="button"
                  tabIndex={0}
                  aria-label={label}
                  data-think-point
                  data-think-side={point.moverColor}
                  data-think-actor={point.moverColor === playerSide ? "player" : "opponent"}
                  onPointerEnter={() => {
                    setActivePoint(point);
                    setHoverKind("time");
                  }}
                  onFocus={() => {
                    setActivePoint(point);
                    setHoverKind("time");
                  }}
                  onBlur={() => {
                    setActivePoint(null);
                    setHoverKind("evidence");
                  }}
                  onClick={() => onSelect(point.index)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onSelect(point.index);
                    }
                  }}
                  style={{ cursor: "pointer", outline: "none" }}
                >
                  <title>{label}</title>
                  <rect
                    x={x}
                    y={THINK_BASE - height}
                    width={width}
                    height={height}
                    fill={white ? PAPER : INK}
                    stroke={white ? INK : PAPER}
                    strokeWidth="1"
                  />
                  <rect
                    data-think-hit="true"
                    x={x - 4}
                    y={THINK_BASE - Math.max(18, height)}
                    width={width + 8}
                    height={Math.max(18, height)}
                    fill="transparent"
                  />
                </g>
              );
            })}
          </g>
          {guidePoint && (
            <line
              data-hover-guide={activePoint ? "pointer" : "selected"}
              x1={timelineGuideX(pointer?.x ?? null, guidePoint, points.length, chartWidth)}
              y1={PAD - 6}
              x2={timelineGuideX(pointer?.x ?? null, guidePoint, points.length, chartWidth)}
              y2={132}
              stroke={RED}
              strokeWidth="2"
              strokeDasharray="4 4"
              pointerEvents="none"
            />
          )}
          {points.map((point) => {
            const x = xAt(point.index, points.length, chartWidth),
              y = evaluationY(point, playerColor);
            const motifName =
              showMotifs && (point.isPlayerMove || includeOpponentEvidence) && point.motifs.length
                ? primaryMotif(point.motifs)
                : null;
            const motifColor = motifName ? timelineMotifColor(point.motifs) : null;
            const spacing = (chartWidth - PAD * 2) / Math.max(1, points.length - 1);
            const size = motifName ? Math.max(4, Math.min(7, spacing * 0.5)) : 4;
            const label = evidencePointLabel(point);
            return (
              <g
                key={point.index}
                role="button"
                tabIndex={0}
                aria-label={label}
                onPointerEnter={() => {
                  setActivePoint(point);
                  setHoverKind("evidence");
                }}
                onFocus={() => {
                  setActivePoint(point);
                  setHoverKind("evidence");
                }}
                onBlur={() => setActivePoint(null)}
                onClick={() => onSelect(point.index)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onSelect(point.index);
                  }
                }}
                style={{ cursor: "pointer", outline: "none" }}
              >
                <title>{label}</title>
                {motifName && (
                  <rect
                    data-evaluation-marker="motif"
                    data-motif-marker={motifName}
                    x={x - size / 2}
                    y={y - size / 2}
                    width={size}
                    height={size}
                    fill={motifColor!}
                    stroke={motifColor!}
                    strokeWidth="1.5"
                  />
                )}
                <rect x={x - 14} y={y - 14} width="28" height="28" fill="transparent" />
              </g>
            );
          })}
          <text x={PAD} y="14" fill="currentColor" opacity=".75" fontSize="11">
            {playerColor === "w" ? "WHITE +" : "BLACK +"}
          </text>
          <rect x={PAD} y={H - 13} width="7" height="7" fill={PAPER} />
          <text x={PAD + 11} y={H - 6} fill="currentColor" opacity=".75" fontSize="10">
            WHITE THINK
          </text>
          <rect x={PAD + 92} y={H - 13} width="7" height="7" fill={INK} stroke={PAPER} />
          <text x={PAD + 103} y={H - 6} fill="currentColor" opacity=".75" fontSize="10">
            BLACK THINK
          </text>
        </Box>
      </Box>
    </Box>
  );
};
